import { Slot, Slottable } from '@radix-ui/react-slot';
import { XClose } from '@timelyapp/icons/solid-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import Tooltip from '../Tooltip';
import styles from './Tag.module.scss';

const cvaTag = cva(styles.container, {
  variants: {
    variant: {
      normal: styles['variant-normal'],
      subtle: styles['variant-subtle'],
      success: styles['variant-success'],
      alert: styles['variant-alert'],
      danger: styles['variant-danger'],
    },
  },
  defaultVariants: { variant: 'normal' },
});

export type TagProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> &
  VariantProps<typeof cvaTag> & {
    asChild?: boolean;
    /** Leading icon or emoji element */
    icon?: React.ReactNode;
    /** Called when the dismiss button is clicked. If provided, a close icon is rendered. */
    onDismiss?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /** Tooltip content shown on hover */
    tooltip?: React.ReactNode;
    children: React.ReactNode;
  };

export const Tag = React.forwardRef<HTMLSpanElement, TagProps>((props, ref) => {
  const { asChild, className, icon, onDismiss, tooltip, variant, children, ...rest } = props;
  const Component = asChild ? Slot : 'span';

  const tag = (
    <Component ref={ref} {...rest} className={cvaTag({ variant, className })}>
      {!!icon && <span className={styles.icon}>{icon}</span>}
      <Slottable>
        <span className={styles.label}>{children}</span>
      </Slottable>
      {!!onDismiss && (
        <button
          type="button"
          className={styles.dismissButton}
          onClick={onDismiss}
          aria-label="Remove"
        >
          <XClose />
        </button>
      )}
    </Component>
  );

  if (tooltip) {
    return (
      <Tooltip tooltip={tooltip} element="span">
        {tag}
      </Tooltip>
    );
  }

  return tag;
});

Tag.displayName = 'Tag';
